import React, { useState } from 'react';
import { Button, Textarea, Input, Alert } from '@/components/ui';
import type {
  AgentPrompt,
  AddPromptVersionRequest,
  UpdateAgentPromptRequest,
  AIModelConfig,
} from '@/services/admin/AdminService';
import { AVAILABLE_MODELS } from '@/services/admin/AdminService';
import { exportAgentInstructionsToPdf } from '@/utils/pdfExport';

interface AgentPromptEditorProps {
  prompt: AgentPrompt;
  onAddVersion: (
    agentName: string,
    data: AddPromptVersionRequest
  ) => Promise<AgentPrompt | null>;
  onUpdatePrompt?: (
    agentName: string,
    data: UpdateAgentPromptRequest
  ) => Promise<AgentPrompt | null>;
  loading?: boolean;
}

export const AgentPromptEditor: React.FC<AgentPromptEditorProps> = ({
  prompt,
  onAddVersion,
  onUpdatePrompt,
  loading = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingModelConfig, setIsEditingModelConfig] = useState(false);
  const [formData, setFormData] = useState({
    prompt_template: '',
    description: '',
    performance_notes: '',
  });
  const [modelConfig, setModelConfig] = useState<AIModelConfig>({
    model: prompt.ai_model_config?.model || 'gemini-2.0-flash',
    temperature: prompt.ai_model_config?.temperature ?? 0.7,
    max_output_tokens: prompt.ai_model_config?.max_output_tokens ?? 4096,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get current active version's prompt template
  const currentVersion = prompt.versions.find((v) => v.is_active);
  const currentTemplate =
    currentVersion?.prompt_template || 'No prompt template configured';

  const handleStartEdit = () => {
    setFormData({
      prompt_template:
        currentTemplate !== 'No prompt template configured'
          ? currentTemplate
          : '',
      description: '',
      performance_notes: '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      prompt_template: '',
      description: '',
      performance_notes: '',
    });
    setErrors({});
  };

  const handleStartEditModelConfig = () => {
    setModelConfig({
      model: prompt.ai_model_config?.model || 'gemini-2.0-flash',
      temperature: prompt.ai_model_config?.temperature ?? 0.7,
      max_output_tokens: prompt.ai_model_config?.max_output_tokens ?? 4096,
    });
    setIsEditingModelConfig(true);
  };

  const handleCancelModelConfig = () => {
    setIsEditingModelConfig(false);
    setModelConfig({
      model: prompt.ai_model_config?.model || 'gemini-2.0-flash',
      temperature: prompt.ai_model_config?.temperature ?? 0.7,
      max_output_tokens: prompt.ai_model_config?.max_output_tokens ?? 4096,
    });
  };

  const handleSaveModelConfig = async () => {
    if (!onUpdatePrompt) return;

    const result = await onUpdatePrompt(prompt.agent_name, {
      ai_model_config: modelConfig,
    });

    if (result) {
      setIsEditingModelConfig(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.prompt_template.trim()) {
      newErrors.prompt_template = 'Prompt template is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Version description is required';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const result = await onAddVersion(prompt.agent_name, {
      prompt_template: formData.prompt_template,
      description: formData.description,
      performance_notes: formData.performance_notes || undefined,
    });

    if (result) {
      setIsEditing(false);
      setFormData({
        prompt_template: '',
        description: '',
        performance_notes: '',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-secondary-900">
            {prompt.title || prompt.agent_name}
          </h3>
          <p className="text-sm text-secondary-500">
            Current version:{' '}
            <span className="font-mono">{prompt.current_version}</span>
            {!prompt.is_enabled && (
              <span className="ml-2 text-warning-600">(Disabled)</span>
            )}
          </p>
        </div>
        {!isEditing && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => exportAgentInstructionsToPdf(prompt)}
            >
              Export PDF
            </Button>
            <Button variant="outline" onClick={handleStartEdit}>
              Edit Prompt
            </Button>
          </div>
        )}
      </div>

      {prompt.description && (
        <p className="text-sm text-secondary-600">{prompt.description}</p>
      )}

      {/* Model Configuration Section - Task 070a */}
      <div className="border border-secondary-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-secondary-900">
            Model Configuration
          </h4>
          {!isEditingModelConfig && onUpdatePrompt && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleStartEditModelConfig}
            >
              Edit
            </Button>
          )}
        </div>

        {isEditingModelConfig ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Model
              </label>
              <select
                value={modelConfig.model}
                onChange={(e) =>
                  setModelConfig((prev) => ({ ...prev, model: e.target.value }))
                }
                className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {AVAILABLE_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Temperature: {modelConfig.temperature?.toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={modelConfig.temperature}
                onChange={(e) =>
                  setModelConfig((prev) => ({
                    ...prev,
                    temperature: parseFloat(e.target.value),
                  }))
                }
                className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-secondary-500 mt-1">
                <span>Deterministic (0)</span>
                <span>Creative (2)</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Max Output Tokens
              </label>
              <Input
                type="number"
                value={modelConfig.max_output_tokens}
                onChange={(e) =>
                  setModelConfig((prev) => ({
                    ...prev,
                    max_output_tokens: parseInt(e.target.value) || 4096,
                  }))
                }
                min={1}
                max={32768}
                fullWidth
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelModelConfig}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveModelConfig}
                loading={loading}
              >
                Save Configuration
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-secondary-500">Model:</span>
              <span className="ml-2 font-mono">
                {prompt.ai_model_config?.model || 'gemini-2.0-flash'}
              </span>
            </div>
            <div>
              <span className="text-secondary-500">Temperature:</span>
              <span className="ml-2 font-mono">
                {prompt.ai_model_config?.temperature ?? 0.7}
              </span>
            </div>
            <div>
              <span className="text-secondary-500">Max Tokens:</span>
              <span className="ml-2 font-mono">
                {prompt.ai_model_config?.max_output_tokens ?? 4096}
              </span>
            </div>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4 border border-secondary-200 rounded-lg p-4 bg-secondary-50">
          <Alert variant="info">
            Creating a new version will save the current prompt as history and
            apply this new version.
          </Alert>

          <Textarea
            label="Prompt Template"
            value={formData.prompt_template}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                prompt_template: e.target.value,
              }))
            }
            error={errors.prompt_template}
            rows={12}
            fullWidth
            className="font-mono text-sm"
          />

          <Input
            label="Version Description"
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
            error={errors.description}
            placeholder="Brief description of changes (e.g., 'Improved output formatting')"
            fullWidth
          />

          <Textarea
            label="Performance Notes (optional)"
            value={formData.performance_notes}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                performance_notes: e.target.value,
              }))
            }
            rows={2}
            fullWidth
            placeholder="Optional notes about expected behavior or performance"
          />

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={loading}>
              Save New Version
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-secondary-200 rounded-lg p-4 bg-secondary-50">
          <pre className="whitespace-pre-wrap text-sm text-secondary-700 font-mono max-h-96 overflow-y-auto">
            {currentTemplate}
          </pre>
        </div>
      )}

      {prompt.placeholders.length > 0 && (
        <div className="text-sm">
          <span className="font-medium text-secondary-700">Placeholders: </span>
          <span className="text-secondary-500">
            {prompt.placeholders.map((p) => `{${p}}`).join(', ')}
          </span>
        </div>
      )}
    </div>
  );
};

export default AgentPromptEditor;
